import { COVER_CROP_MIX_TYPE, CoverCropMixType, ECONOMICS_FIELDS } from "consts";

import { SoilSightCoverCropDefaults, SoilSightCropDefaults, SoilSightCropDefaultsByCropId, SoilSightNonCropDefaults } from "models";

import { CropYear } from "pages/app/scenario-analysis/types/scenario";

export type InheritedCoverCropDefaultsByKey = Record<string, SoilSightCoverCropDefaults>;

/** One value per economics field, null where the picker has none. */
const economicsValues = (pick: (field: keyof SoilSightCropDefaults) => number | null | undefined): SoilSightCropDefaults =>
  ECONOMICS_FIELDS.reduce((values, field) => ({ ...values, [field]: pick(field) ?? null }), {} as SoilSightCropDefaults);

/** Key of the inherited cover crop defaults for one main crop + cover crop type. */
export const coverCropDefaultsKey = (cropId: string, mixType: string): string => `${cropId}::${mixType}`;

/** Inherited (cross-scenario) values win over API defaults, field by field. */
export const mergeCropDefaults = (
  cropId: string,
  inheritedDefaultsByCropId?: SoilSightCropDefaultsByCropId,
  cropDefaultsByCropId?: SoilSightCropDefaultsByCropId
): SoilSightCropDefaults | undefined => {
  const inherited = inheritedDefaultsByCropId?.[cropId];
  const api = cropDefaultsByCropId?.[cropId];
  if (!inherited && !api) return undefined;
  return economicsValues((field) => inherited?.[field] ?? api?.[field]);
};

/** Cover crop defaults for a main crop + cover crop type; inherited values win over API defaults. None for "no cover crop". */
export const mergeCoverCropDefaults = (
  cropId: string,
  mixType: CoverCropMixType | string | null | undefined,
  inheritedCoverCropDefaultsByKey?: InheritedCoverCropDefaultsByKey,
  coverCropDefaults?: SoilSightNonCropDefaults["coverCropDefaults"] | null
): SoilSightCoverCropDefaults | undefined => {
  if (!mixType || mixType === COVER_CROP_MIX_TYPE.NONE) return undefined;
  const inherited = inheritedCoverCropDefaultsByKey?.[coverCropDefaultsKey(cropId, mixType)];
  const api = coverCropDefaults?.[mixType as keyof SoilSightNonCropDefaults["coverCropDefaults"]];
  if (!inherited && !api) return undefined;
  return { legumeShare: inherited?.legumeShare ?? api?.legumeShare ?? null, ccCost: inherited?.ccCost ?? api?.ccCost ?? null };
};

/** Economics values the drawer opens with: the year's saved value, else the effective default. */
export const buildInitialEconomicsValues = (cropYear: CropYear, defaults: SoilSightCropDefaults | undefined): SoilSightCropDefaults =>
  economicsValues((field) => cropYear[field] ?? defaults?.[field]);

/** Economics fields to move to the new crop's defaults: empty ones and ones still on the previous crop's default. */
export const getCropChangeUpdates = (
  getCurrentValue: (field: keyof SoilSightCropDefaults) => number | null | undefined,
  previousDefaults: SoilSightCropDefaults | undefined,
  nextDefaults: SoilSightCropDefaults | undefined
): Partial<Record<keyof SoilSightCropDefaults, number | null>> => {
  const updates: Partial<Record<keyof SoilSightCropDefaults, number | null>> = {};
  ECONOMICS_FIELDS.forEach((field) => {
    const current = getCurrentValue(field);
    const wasOnPreviousDefault = previousDefaults != null && current != null && current === previousDefaults[field];
    if (current == null || wasOnPreviousDefault) {
      updates[field] = nextDefaults?.[field] ?? null;
    }
  });
  return updates;
};

/** Drops pre-fills the user never edited, so a displayed default is not persisted as this year's override. */
export const withoutUntouchedPrefills = (values: Partial<CropYear>, cropYear: CropYear, openedWith: Partial<CropYear>): Partial<CropYear> => {
  const pruned: Partial<CropYear> = { ...values };
  (Object.keys(openedWith) as Array<keyof CropYear>).forEach((field) => {
    if (cropYear[field] == null && pruned[field] === openedWith[field]) delete pruned[field];
  });
  return pruned;
};
